// Derive display metadata (version, category) for a service from its image_ref + tags. Shared by the
// catalog and the node-services list so both show the same version string and group by the same category.

/**
 * The version a service runs at, parsed from its image_ref. Handles a registry host:port prefix
 * (gitlab.local:5050/anais/infra/echo:dev → "dev") and a pinned digest (…@sha256:abcd… → "sha256:abcd…").
 * Falls back to "latest" when the ref carries no tag/digest.
 */
export function imageVersion(imageRef: string | undefined | null): string {
  if (!imageRef) return '—';
  const ref = imageRef.trim();
  const at = ref.indexOf('@');
  if (at >= 0) {
    const dig = ref.slice(at + 1);
    return dig.length > 19 ? `${dig.slice(0, 19)}…` : dig; // sha256:xxxxxxxxxx…
  }
  // The tag is a ":<tag>" AFTER the last "/", so a "host:port/" prefix is not mistaken for a tag.
  const lastSlash = ref.lastIndexOf('/');
  const tail = lastSlash >= 0 ? ref.slice(lastSlash + 1) : ref;
  const colon = tail.indexOf(':');
  return colon >= 0 ? tail.slice(colon + 1) : 'latest';
}

/** Compact relative age ("3d", "5h", "just now") for a timestamp; "" when absent/unparseable. */
export function relativeTime(ts: string | number | Date | undefined | null): string {
  if (ts == null) return '';
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

// ST44: map a provider/service state to a display label + badge tone + tooltip. The state is the
// gateway-derived rich `state`, falling back to the coarse `runtime_state`. Crucially an
// `idle-stopped` service (intentionally stopped to free VRAM, re-issuable) is NOT styled as an error
// ('muted', not 'bad') — only genuine failures/crashes are 'bad'.
export type BadgeTone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';
export interface ProviderStateMeta {
  label: string;
  tone: BadgeTone;
  title: string;
}

const PROVIDER_STATE_META: Record<string, ProviderStateMeta> = {
  live: { label: 'Live', tone: 'ok', title: 'Warm and ready — advertising free capacity.' },
  loaded: { label: 'Live', tone: 'ok', title: 'Warm and ready — advertising free capacity.' },
  running: { label: 'Running', tone: 'ok', title: 'Live and running a task.' },
  busy: { label: 'Running', tone: 'ok', title: 'Live and running a task.' },
  idle: { label: 'Idle', tone: 'warn', title: 'Live but advertising no free capacity.' },
  warming: { label: 'Warming', tone: 'warn', title: 'Container starting / loading — not yet ready.' },
  starting: { label: 'Warming', tone: 'warn', title: 'Container starting / loading — not yet ready.' },
  restarting: { label: 'Restarting', tone: 'warn', title: 'Container is restarting.' },
  'idle-stopped': {
    label: 'Idle-stopped',
    tone: 'muted',
    title: 'Stopped to free VRAM — known and re-issuable on demand (not an error).',
  },
  failed: { label: 'Failed', tone: 'bad', title: 'Container failed to start.' },
  crashed: { label: 'Crashed', tone: 'bad', title: 'Container crashed.' },
  stale: { label: 'Stale', tone: 'muted', title: 'No recent heartbeat — offline / unknown.' },
  cold: { label: 'Cold', tone: 'muted', title: 'Model not resident — cold on disk.' },
  'on-disk': { label: 'On disk', tone: 'muted', title: 'Model not resident — image on disk.' },
};

/** Display metadata for a provider/service state; unknown tokens degrade to a neutral 'info' pill. */
export function providerStateMeta(state: string | undefined | null): ProviderStateMeta {
  const key = (state ?? '').trim().toLowerCase().replace(/_/g, '-');
  const hit = PROVIDER_STATE_META[key];
  if (hit) return hit;
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Unknown';
  return { label, tone: 'info', title: label };
}

/** The category a service is grouped under — its first tag, title-cased; "Other" when untagged. */
export function serviceCategory(tags: string[] | undefined | null): string {
  const first = (tags ?? []).map((t) => t.trim()).filter(Boolean)[0];
  if (!first) return 'Other';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** Group items by category, returning [category, items[]] sorted by category, items sorted by name. */
export function groupByCategory<T>(
  items: T[],
  tagsOf: (t: T) => string[] | undefined | null,
  nameOf: (t: T) => string,
): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const it of items) {
    const cat = serviceCategory(tagsOf(it));
    (groups.get(cat) ?? groups.set(cat, []).get(cat)!).push(it);
  }
  return [...groups.entries()]
    .map(([cat, list]) => {
      list.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
      return [cat, list] as [string, T[]];
    })
    .sort((a, b) => {
      // "Other" always sinks to the bottom; the rest alphabetical.
      if (a[0] === 'Other') return 1;
      if (b[0] === 'Other') return -1;
      return a[0].localeCompare(b[0]);
    });
}
